import { Inject, Injectable } from '@angular/core';
import { Observable, BehaviorSubject, of, throwError } from 'rxjs';
import { Holdings } from './../../models/common/trading-holdings';
import {
    Account,
    TradingOrderBase,
    TradingExchangeOrder,
    TradingDayInfo,
    TransactionType,
    TypeAheadSecurityType,
    RegulatoryInfo,
    NetxAddDigSig,
    AccountType,
    TradeType,
    Quote,
    OptionStrategy,
    ActionPerformed,
    ButtonFocus,
    OrderReturnCode,
    EqtOptTradeReturnCode,
    MutualFundTradeReturnCode
} from '../../models/common/trading-common.model';

import { TradingServiceStrategy } from './trading-service-strategy.service';
import { TradingEntitlements } from '../../models/common/trading-entitlements';
import { OrderActionParams } from '../../models/common/order-status.model';
import { CONTAINER_TYPE } from '../../utils/trading-constants';
import { HttpErrorResponse } from '@angular/common/http';
import { isAdjustedSymbol } from '../../utils/option/option-multi-leg.utils';
@Injectable()
export class TradingService {

    private baseUrl: string;
    public accountSelectSubscribed: boolean = false;
    public holdingsLoadInProgress: boolean = false;
    public tradingEntitlements: TradingEntitlements;
    public currentContainer: CONTAINER_TYPE;
    private viewPropectusLink = 'https://prospectus-express.broadridge.com/get_template.asp';

    quoteSubject$: BehaviorSubject<any> = new BehaviorSubject({});
    // In case of we have 2 Quote components in same form, e.g. Mutual Fund Exchange 'From' and 'To' Quotes
    secondQuoteSubject$: BehaviorSubject<TradingOrderBase> = new BehaviorSubject({} as TradingOrderBase);
    // multiLeg Option Quote
    optionLegQuoteSubject$: BehaviorSubject<{optionLegIndex:number, quote:Quote}> = new BehaviorSubject({} as {optionLegIndex:number, quote:Quote});

    holdingSubject$: BehaviorSubject<any[]> = new BehaviorSubject({} as any[]);
    holdingLoadCompleteSubject$: BehaviorSubject<any> = new BehaviorSubject({} as any);
    holdingErrorSubject$: BehaviorSubject<any> = new BehaviorSubject(null);

    accountSubject$: BehaviorSubject<Account> = new BehaviorSubject({} as Account);

    symbolSubject$: BehaviorSubject<string[]> = new BehaviorSubject(null);
    // In case of we have 2 symbol fields in same form, e.g. Mutual Fund Exchange 'From' and 'To' symbol fields
    secondSymbolSubject$: BehaviorSubject<string[]> = new BehaviorSubject(null);

    isChangeOrCorrectOrder$: BehaviorSubject<boolean> = new BehaviorSubject(null);

    private newOrderSubject = new BehaviorSubject<TradingOrderBase | null>(null);
    newOrderResult$ = this.newOrderSubject.asObservable();

    private openOrderStatusSubject = new BehaviorSubject<string | null>(null);
    openOrderStatus$ = this.openOrderStatusSubject.asObservable();

    private orderActionSubject = new BehaviorSubject<OrderActionParams | null>(null);
    orderActionResult$ = this.orderActionSubject.asObservable();

    private sessionTypeSubject = new BehaviorSubject(null);
    sessionTypeSubject$ = this.sessionTypeSubject.asObservable();

    private tradingDayInfoSubject = new BehaviorSubject(null);
    tradingDayInfoSubject$ = this.tradingDayInfoSubject.asObservable();

    private tradingDefaultsSubject = new BehaviorSubject(null);
    tradingDefaultsObservable$ = this.tradingDefaultsSubject.asObservable();

    private orderDetailsSubject = new BehaviorSubject(null);
    orderDetailsObservable$ = this.orderDetailsSubject.asObservable();

    private orderDetailKeySubject = new BehaviorSubject(null);
    orderDetailsKeyObservable$ = this.orderDetailKeySubject.asObservable();

    private onRawOrderSubject = new BehaviorSubject<TradingOrderBase | null>(null);
    onRawOrderObservable$ = this.onRawOrderSubject.asObservable();

    // this is only for retail account's list.
    // don't use this for any of the professional logics
    private onAccountListSubject = new BehaviorSubject<Account[] | null>(null);
    onAccountListSubject$ = this.onAccountListSubject.asObservable();
    accountsList: Account[] = null;

    private onEntitlementsSubject = new BehaviorSubject<any | null>(null);
    onEntitlementsSubject$ = this.onEntitlementsSubject.asObservable();

    private onScrollSubject = new BehaviorSubject<any | null>(null);
    onScrollSubject$ = this.onScrollSubject.asObservable();

    private exchangeAgreementDialogState = new BehaviorSubject<string>(null);
    exchangeAgreementDialogState$ = this.exchangeAgreementDialogState.asObservable();

    private orderEntrylegalEntities = new BehaviorSubject(null);
    orderEntrylegalEntities$ = this.orderEntrylegalEntities.asObservable();

    private onClearOrderEntryFormSubject = new BehaviorSubject<boolean>(null);
    onClearOrderEntryFormSubject$ = this.onClearOrderEntryFormSubject.asObservable();

    private focusOnSubject = new BehaviorSubject<boolean>(null);
    onFocusSubject$ = this.focusOnSubject.asObservable();

    private selectedEligibleaccount = new BehaviorSubject<Account>(null);
    selectedEligibleaccount$ = this.selectedEligibleaccount.asObservable();

    private estPrincipleAmountSubject = new BehaviorSubject<number>(null);
    estPrincipleAmountSubject$ = this.estPrincipleAmountSubject.asObservable();

    private actionPerformedSubject = new BehaviorSubject<ActionPerformed>(null);
    actionPerformedSubject$ = this.actionPerformedSubject.asObservable();

    constructor(@Inject(TradingServiceStrategy) private strategies: Array<TradingServiceStrategy>) {

    }

    private getStrategy(platform: string): TradingServiceStrategy {
        return this.strategies.find((strategy) => {
            return strategy.support(platform);
        });
    }

    public toggleSessionType(selectedSession: string) {
        this.sessionTypeSubject.next(selectedSession);
    }

    public scrollNotification(value: any) {
        this.onScrollSubject.next(value);
    }

    public getEntitlements(platform: string): void {
        this.getStrategy(platform).getEntitlements().subscribe(success => {
            this.onEntitlementsSubject.next(success);
        }, error => {
            this.onEntitlementsSubject.next(error);
        });
    }

    public getHoldings(platform: string, acctNum: string, firmAcct: boolean, assetType?: string,includeTradingSpecificData?: boolean): Observable<any> {
        return this.getStrategy(platform).getHoldings(acctNum, firmAcct, assetType, includeTradingSpecificData);
    }

    public getQuote(platform: string, acctNum: string, symId: string, quoteType: string, selectedSession: string, complianceSiteIdInd: boolean): Observable<any> {
        return this.getStrategy(platform).getQuote(acctNum, symId, quoteType, selectedSession, complianceSiteIdInd);
    }

    public getBidPrc(quote: Quote): number {
        let bidPrice = 0;
        if (quote) {
            if (quote.sqtBidPrc > 0) {
                bidPrice = quote.sqtBidPrc;
            } else if (quote.sqtLastPrc > 0) {
                bidPrice = quote.sqtLastPrc;
            } else if (quote.eqtClsPrc > 0) {
                bidPrice = quote.eqtClsPrc;
            }
        }
        return bidPrice;
    }

    public getAskPrc(quote: Quote) {
        let askPrice = 0;
        if (quote) {
            if (quote.sqtAskPrc > 0) {
                askPrice = quote.sqtAskPrc;
            } else if (quote.sqtLastPrc > 0) {
                askPrice = quote.sqtLastPrc;
            } else if (quote.eqtClsPrc > 0) {
                askPrice = quote.eqtClsPrc;
            }
        }
        return askPrice;
    }

    public getAccountDetails(platform: string, acctNum: string): Observable<any> {
        return this.getStrategy(platform).getAccountDetails(acctNum);
    }

    public getSymbolQuery(platform: string, acctNum: string, sym: string, secType: TypeAheadSecurityType, excludeNonUSD: boolean): Observable<any> {
        return this.getStrategy(platform).getSymbolQuery(acctNum, sym, secType, excludeNonUSD);
    }

    public getOrderDetails(platform: string, acctNum: string, ordDtlKey: string, subRequestTypeCode: string, ordProdCodeTex: string): Observable<any> {
        return this.getStrategy(platform).getOrderDetails(acctNum, ordDtlKey, subRequestTypeCode, ordProdCodeTex);
    }

    public getEstimatedCommission(platform: string, order: TradingExchangeOrder): Observable<any> {
        return this.getStrategy(platform).getEstimatedCommission(order);
    }

    public getViewPropectusLink() {
        return this.viewPropectusLink;
    }

    // Returns an Observable that will dynamically provide a list of held positions matching the parameters and eligible for close
    // (not necessarily sell, as short positions are closed through a Buy-side order like Buy to Close or Buy to Cover)
    public getSymbolForSell(platform: string, acctNum: string, searchString: string, secTypes: Array<TypeAheadSecurityType>,
                              transactionType: string, extraFilterObj?: any): Observable<any> {

        if (this.holdingErrorSubject$ && this.holdingErrorSubject$.value && this.holdingErrorSubject$.value.hasError) {
            console.log('throwing 500 return code error');
            return throwError(new HttpErrorResponse({status: 500}));
        }

        let holdings: Array<Holdings> = this.holdingSubject$.value;
        let filteredPositions: Array<Holdings> = new Array<Holdings>();

        if (holdings && holdings.filter) {
            filteredPositions = holdings.filter((item) => {
                if (!!searchString) {
                    return this.itemMatchesFilterBySearchStringAndPositionQuantity(item, searchString, transactionType, secTypes, extraFilterObj);
                } else {
                    return this.itemMatchesFilterByHoldingQuantityAndTransaction(item, transactionType, secTypes, extraFilterObj);
                }
            });

            // We've gotten the initial filtered list of positions. Now do extra filering for complex option order closing
            if ( extraFilterObj && !extraFilterObj.isRollOrder ) {
                filteredPositions = filteredPositions.filter(item => {
                    return this.itemMatchesFilterByComplexOptionStrategy(item, searchString, extraFilterObj);
                });
            }
        }

        let resp = {
            errorCode: 0,
            errorDescription: 'SUCCESS',
            errorMessage: null,
            response: filteredPositions
        };

        return of(resp);
    }

    private itemMatchesFilterBySearchStringAndPositionQuantity(item: any, searchString: string, transactionType: string, secTypes: Array<TypeAheadSecurityType>, extraFilterObj?: any) {
        let secType: boolean = false;
        let isStrategyValidForAdjusted: boolean = true;

        // When the extraFilterObj is provided (only used with complex option orders), need to see if we're
        // specifically searching index securities, as that requires some different matching logic than
        // other securities.
        if ( extraFilterObj ) {
            secType = extraFilterObj.secType === 'INDEX' ? true : false;
            //closing transaction and position selector popup doesn't show any adjusted option positions for Buywrite and Married put
            isStrategyValidForAdjusted = extraFilterObj.strategy != OptionStrategy.BUY_WRITE && extraFilterObj.strategy != OptionStrategy.MARRIED_PUT;
        }

        let symbTermPresent: boolean = this.isSearchTermPresentInPosition(item, searchString, secType, secTypes, isStrategyValidForAdjusted);

        // Need to check for a Roll order here, as the transaction type-based filtering done by
        // itemMatchesAdditionalTransactionSpecificFiltering will incorrectly return only negative quantity positions
        // when there is no transaction type provided, as is the case with a Roll order where we show all option
        // positions before the user has selected the transaction type.
        if (this.isSecurityType(TypeAheadSecurityType.OPTION, secTypes) && !!extraFilterObj && extraFilterObj.isRollOrder) {
            // Special handling for a ROLL option order, as we will have no transaction type selected in this case.
            // Return all non-zero holdings.
            if ( symbTermPresent ) {
                console.warn('Found a match on searchString "' + searchString + '". Position has quantity ' + item.tradeDateQuantity);
            }
            return symbTermPresent && item.tradeDateQuantity !== 0;
        }

        return this.itemMatchesAdditionalTransactionSpecificFiltering(item, transactionType, symbTermPresent);
    }

    private isSearchTermPresentInPosition(item: any, searchString: string, secType: boolean, secTypes: Array<TypeAheadSecurityType>, isStrategyValidForAdjusted: boolean): boolean {
        if ( !item.security || !this.isSecurityType(item.security.assetType, secTypes) ) {
            return false;
        }

        const isMatchOnSymbol: boolean = item.symbol != null && item.symbol.toUpperCase().indexOf(searchString.toUpperCase()) > -1;
        const isMatchOnDescription: boolean = item.security.securityDescription.toUpperCase().indexOf(searchString.toUpperCase()) > -1;

        // Adjusted option symbols should be returned when search query is on the main underlying symbol.
        // E.g., query for GE should also return a match for GE1, GE2, etc.
        //closing transaction and position selector popup doesn't show any adjusted option positions for Buywrite and Married put
        const isAdjustedSymbolMatch: boolean = item.symbol != null && isAdjustedSymbol(item.symbol, searchString) && isStrategyValidForAdjusted;

        // Index option symbols should be returned when search query is on an index.
        // E.g., query for SPX should also return a match for SPXW.
        const isIndexSymbolMatch: boolean = secType &&  item.symbol != null &&
            (item.symbol.toUpperCase().indexOf((searchString + 'W').toUpperCase()) > -1
                || item.symbol.toUpperCase().indexOf((searchString + 'P').toUpperCase()) > -1)

        return isMatchOnSymbol || isMatchOnDescription || isAdjustedSymbolMatch || isIndexSymbolMatch;
    }

    private itemMatchesAdditionalTransactionSpecificFiltering(item: any, transactionType: string, symbTermPresent: boolean): boolean {
        switch(transactionType) {
            case TransactionType.CLOSE_CALLS_OR_PUTS:
                // When searching for Close Calls & Puts positions, we have to return both positive and negative
                // Option positions as long as they match the symbol term.
                return symbTermPresent && item.tradeDateQuantity !== 0;
            case TransactionType.SELL_TO_CLOSE:
                return symbTermPresent && item.tradeDateQuantity > 0;
            case TransactionType.BUY_TO_CLOSE:
                return symbTermPresent && item.tradeDateQuantity < 0;
            case TransactionType.SELL_COVERED_CALLS:
                // Sell Covered Calls orders have to return only Long positions in USD
                return symbTermPresent && item.symbol !== item.cusip && item.tradeDateQuantity > 0 &&
                    item.positionCcy === 'USD' && !!item.accountType && item.accountType.toUpperCase() !== AccountType.SHORT &&
                    item.security.underlyingCusipAvailable;
            case TransactionType.BUY_TO_COVER:
                            return symbTermPresent && item.tradeDateQuantity < 0;
            default:
                            return symbTermPresent && item.tradeDateQuantity > 0;
        }
    }

    private itemMatchesFilterByHoldingQuantityAndTransaction(item: any, transactionType: string, secTypes: Array<TypeAheadSecurityType>, extraFilterObj?: any): boolean {
        let hasHoldings: boolean = !!item.security && !!item.symbol && this.isSecurityType(item.security.assetType, secTypes);
        if (!hasHoldings){
            return false;
        }

        if (!this.isSecurityType(TypeAheadSecurityType.OPTION, secTypes) && transactionType !== TransactionType.BUY_TO_COVER) {
            // Must be an Equity search or MF search.
            // Return only holdings with a positive quantity (long holding).
            return hasHoldings && item.tradeDateQuantity > 0;
        }

        if (!this.isSecurityType(TypeAheadSecurityType.OPTION, secTypes) && !!extraFilterObj && extraFilterObj.isRollOrder) {
            // Special handling for a ROLL option order, as we will have no transaction type selected in this case.
            // Return all non-zero holdings.
            return hasHoldings && item.tradeDateQuantity != 0;
        }

        switch(transactionType) {
            case TransactionType.SELL_COVERED_CALLS:
                // Has to be an Equity search, since Sell Covered Calls is always done
                // against equity holdings. Return only holdings with a positive quantity, USD currency, and
                // not held in a Short account, with a proper symbol, and with an underlying Cusip available
                // (which indicates the security is optionable)
                return hasHoldings && item.symbol !== item.cusip && item.tradeDateQuantity > 0 && item.positionCcy === 'USD' &&
                    item.accountType && item.accountType.toUpperCase() !== AccountType.SHORT && item.security.underlyingCusipAvailable;
            case TransactionType.BUY_TO_COVER:
                // Can only be an Equity search, as that's the only sec type with Buy to Cover transaction type.
                // Return only holdings with a negative quantity (Short Sells).
                return hasHoldings && item.tradeDateQuantity < 0;
            default:
                // If we're here, it can only be an Option search, and the only sell-side transaction it can be
                // is Close Calls Or Puts. So we have to return both positive and negative holdings, since there
                // aren't separate transaction types for closing one vs the other like we have with equities.
                return hasHoldings && item.tradeDateQuantity !== 0;
        }
    }

    private itemMatchesFilterByComplexOptionStrategy(item: any, searchString: string, extraFilterObj: any): boolean {
        let underlyingSymbol, expDateFromSymbol, chainTypeFromSymbol, strikePriceFromSymbol;

        if (item.symbol?.indexOf(' C ') > 0 || item.symbol?.indexOf(' P ') > 0) {
            let dataFromSymbol = item.symbol?.split(' '); // 'AAPL 08192022 C 120' => ['AAPL', '08192022', 'C', '120']
            underlyingSymbol = dataFromSymbol[0];
            expDateFromSymbol = dataFromSymbol[1];
            chainTypeFromSymbol = dataFromSymbol[2];
            strikePriceFromSymbol = dataFromSymbol[3];
        } else {
            underlyingSymbol = item.symbol;
        }

        let isValidAdjustedStrategy = extraFilterObj.strategy !== OptionStrategy.BUY_WRITE && extraFilterObj.strategy !== OptionStrategy.MARRIED_PUT;

        if (
            extraFilterObj.secType && extraFilterObj.secType === 'INDEX' &&
            (searchString + 'W' === underlyingSymbol || searchString + 'P' === underlyingSymbol)
        ) {
            return this.applyFiltering(chainTypeFromSymbol, expDateFromSymbol, strikePriceFromSymbol, extraFilterObj);
        } else if (item.symbol != null && extraFilterObj.index === 0 && isAdjustedSymbol(item.symbol, searchString) && isValidAdjustedStrategy) {
            // For adjusted symbols - Only for leg1 it should list the complete symbols, leg2 should be based on leg1 underlying symbol
            return this.applyFiltering(chainTypeFromSymbol, expDateFromSymbol, strikePriceFromSymbol, extraFilterObj);
        } else if (underlyingSymbol !== searchString) {  // make sure underlyingSymbol exactly match
            return false;
        }

        return this.applyFiltering(chainTypeFromSymbol, expDateFromSymbol, strikePriceFromSymbol, extraFilterObj);
    }

    private applyFiltering(chainTypeFromSymbol: string, expDateFromSymbol: string, strikePriceFromSymbol: string, extraFilterObj: any): boolean {
        if (extraFilterObj.strategy === OptionStrategy.BUY_WRITE || extraFilterObj.strategy === OptionStrategy.MARRIED_PUT || 
            (extraFilterObj.chainType && extraFilterObj.strikePrice && extraFilterObj.expDate)) {
            return this.isChainTypeExpDateAndStrikeValidForStrategyAndExtraFilters(chainTypeFromSymbol, expDateFromSymbol, strikePriceFromSymbol, extraFilterObj);
        }
        return true;
    }

    private isChainTypeExpDateAndStrikeValidForStrategyAndExtraFilters(chainTypeStr: string, expDateStr: string, strikePriceStr: string, extraFilterObj: any): boolean {
        switch(extraFilterObj.strategy) {
            case OptionStrategy.CALENDAR_SPREAD:
                return chainTypeStr === extraFilterObj.chainType && expDateStr !== extraFilterObj.expDate
                    && Number(strikePriceStr) === extraFilterObj.strikePrice;
            case OptionStrategy.VERTICAL_SPREAD:
                return chainTypeStr === extraFilterObj.chainType && expDateStr === extraFilterObj.expDate
                    && Number(strikePriceStr) !== extraFilterObj.strikePrice;
            case OptionStrategy.DIAGONAL_SPREAD:
                return chainTypeStr === extraFilterObj.chainType && expDateStr !== extraFilterObj.expDate
                    && Number(strikePriceStr) !== extraFilterObj.strikePrice;
            case OptionStrategy.STRANGLE:
            case OptionStrategy.COLLAR:
                return chainTypeStr !== extraFilterObj.chainType && expDateStr === extraFilterObj.expDate
                    && Number(strikePriceStr) !== extraFilterObj.strikePrice;
            case OptionStrategy.STRADDLE:
                return chainTypeStr !== extraFilterObj.chainType && expDateStr === extraFilterObj.expDate
                    && Number(strikePriceStr) === extraFilterObj.strikePrice;
            case OptionStrategy.BUY_WRITE:
            case OptionStrategy.MARRIED_PUT:
                return chainTypeStr === extraFilterObj.chainType;
            case OptionStrategy.RATIO:
                if (extraFilterObj.isRatioSameSideStrategy) {
                    return chainTypeStr !== extraFilterObj.chainType; // Same side transaction order must have different chain type
                } else {
                    return true;
                }
            default:
                return true;
        }
    }

    private isSecurityType(secType: string, secTypeItems: Array<TypeAheadSecurityType>):boolean {

        if (!secTypeItems || !secType){
            return false;
        }
        for (let securityType of secTypeItems){
            if (securityType === secType){
                return true;
            }
        }
        return false;

    }

    public filterFundsForMFRedemption(filterString: string, transactionType: string): Observable<any> {
        if (this.holdingErrorSubject$ && this.holdingErrorSubject$.value && this.holdingErrorSubject$.value.hasError) {
            console.log('throwing 500 return code error');
            return throwError(new HttpErrorResponse({status: 500}));
        }

        let holdings: Array<Holdings> = this.holdingSubject$.value;
        let filteredPositions: Array<Holdings> = new Array<Holdings>();

        let secType: TypeAheadSecurityType = TypeAheadSecurityType.MUTFUND;
        if (holdings && holdings.filter) {
            filteredPositions = holdings.filter((item) => {
                if (!!filterString && item.security != null && item.security.assetTypeCode === secType) {
                    const ucFilterString: string = filterString.toUpperCase();
                    return this.fundPositionHasSearchTermOnSymbolCusipIsonOrDescription(item, ucFilterString);
                } else {
                    return item.security != null && item.security.assetTypeCode === secType && item.cusip != null;
                }
            });
        }

        if ( transactionType === TransactionType.EXCHANGE || transactionType === TransactionType.EXCHANGE_ALL ) {
            // Exchange orders must be with USD-based funds only, including offshore funds that trade in USD
            filteredPositions = filteredPositions.filter((item) => {
                return item.positionCcy === 'USD';
            });
        }

        let resp = {
            errorCode: 0,
            errorDescription: 'SUCCESS',
            errorMessage: null,
            response: filteredPositions
        };

        return of(resp);
    }

    private fundPositionHasSearchTermOnSymbolCusipIsonOrDescription(item: any, ucFilterString: string): boolean {
        const isMatchOnSymbol = item.symbol != null && item.symbol.toUpperCase().indexOf(ucFilterString) > -1;
        const isMatchOnCusip = item.cusip != null && item.cusip.toUpperCase().indexOf(ucFilterString) > -1;
        const isMatchOnIsin = item.isin != null && item.isin.toUpperCase().indexOf(ucFilterString) > -1;
        const isMatchOnDescription = item.security.securityDescription != null
            && item.security.securityDescription.toUpperCase().indexOf(ucFilterString) > -1;

        return isMatchOnSymbol || isMatchOnCusip || isMatchOnIsin || isMatchOnDescription;
    }

    public newOrderNotification(newOrder: TradingOrderBase | null) {
        this.newOrderSubject.next(newOrder);
    }

    public openOrderStatusNotification(status: string | null) {
        this.openOrderStatusSubject.next(status);
    }

    public orderActionNotification(orderInfo: OrderActionParams | null) {
        this.orderActionSubject.next(orderInfo);

        document.dispatchEvent(new CustomEvent (('onEditOrder'), {
            detail: {
                eventValue: 'onEditOrder'
            }
        }));
    }

    public tradingDayInfoChangeNotification(tradingDayInfo: TradingDayInfo) {
        this.tradingDayInfoSubject.next(tradingDayInfo);
    }

    public tradingDefaultsNotification(theTradingDefaults: any) {
        this.tradingDefaultsSubject.next(theTradingDefaults);
    }
    public orderDetailsNotification(orderDetails: any) {
        this.orderDetailsSubject.next(orderDetails);
    }

    public rowDetailsNotification(params: any) {
        this.orderDetailKeySubject.next(params);
    }

    public onRawOrderNotification(rawOrder: TradingOrderBase) {
        this.onRawOrderSubject.next(rawOrder);
    }

    // this is only for retail account's list.
    // don't use this for any of the professional logics
    public onAccountListNotification(accountList: Account[]) {
        this.onAccountListSubject.next(accountList);
    }

    public getTradingDayInfo(platform: string) {
        return this.getStrategy(platform).getTradingDayInfo();
    }

    public getTradingSettings(platform: string, ibdId: string, assetType: string, role?: string) {
        return this.getStrategy(platform).getTradingSettings(ibdId, assetType, role);
    }

    public saveTradingSettings(platform: string, ibdId: string, assetType: string, role: string, reqPayload: any) {
        return this.getStrategy(platform).saveTradingSettings(ibdId, assetType, role, reqPayload);
    }

    public getApplicableTradeTypes(platform: string, tradingEntitlements: TradingEntitlements) {
        return this.getStrategy(platform).getApplicableTradeTypes(tradingEntitlements);
    }

    public getOrderDateTimeStamp(platform: string, accountNum: string, tranCode: string) {
        return this.getStrategy(platform).getOrderDateTimeStamp(accountNum, tranCode);
    }

    public validateOrderDateTimes(platform: string, regInfo: RegulatoryInfo) {
        return this.getStrategy(platform).validateOrderDateTimes(regInfo);
    }

    public getNextTradingDate(platform: string): Observable<any> {
        return this.getStrategy(platform).getNextTradingDate();
    }

    public getLegalEntity(platform: string, ibdId: string, acctNum: string): Observable<any> {
        return this.getStrategy(platform).getLegalEntity(ibdId, acctNum);
    }

    public getAveragePriceAccountsForUser(platform: string): Observable<any> {
        return this.getStrategy(platform).getAveragePriceAccountsForUser();
    }

    public updateTermsAndConditionsAcceptance(platform: string, acceptances: NetxAddDigSig[]): Observable<any> {
        return this.getStrategy(platform).updateTermsAndConditionsAcceptance(acceptances);
    }

    public getAccounts(platform: string): void {

        if (this.accountsList) {
            return this.onAccountListNotification(this.accountsList);
        }

        this.getStrategy(platform).getAccounts().subscribe(data => {
            this.persistAccountList(data.response);
            return this.onAccountListNotification(data.response as Account[]);
        }, error => {
            return this.onAccountListNotification(null);
        });
    }

    public updateAccountsList(accountsList: Account[], isTrading: boolean, isTrading529: boolean): Account[] {
        let tempIndvAccountlist = [];

        for (let account of accountsList) {

            if (isTrading) {
                if ( this.isAccountValidForTrading(account, isTrading529) ) {
                    tempIndvAccountlist.push(account);
                }
            } else {
                tempIndvAccountlist.push(account);
            }
        }
        return tempIndvAccountlist;
    }

    private isAccountValidForTrading(account: Account, isTrading529: boolean): boolean {
        if ( account.tradingEnabled ) {
            if ( isTrading529 ) {
                return account.trading529Enabled;
            } else {
                return true;
            }
        }

        return false;
    }

    // the account list will not change during the login. Persisting in the Service's singleton instance to avoid multiple calls to server.
    // refreshing the page will clear this cache.
    private persistAccountList(accounts: Account[]): void {
        this.accountsList = accounts;
    }

    getCountriesList(platform: string): Observable<any> {
        return this.getStrategy(platform).getCountriesList();
    }

    opraData(platform: string, NONPROFDATA: any, NASDAQDATA: any, OPRADATA: any, NSPDDATA: any, PROFDATA: any): Observable<any> {
        return this.getStrategy(platform).opraData(NONPROFDATA, NASDAQDATA, OPRADATA, NSPDDATA, PROFDATA);
    }

    setExchangeAgreementDialogState(input: string) {
        this.exchangeAgreementDialogState.next(input);
    }

    setLegalEntities(input) {
        this.orderEntrylegalEntities.next(input);
    }

    onClearOrderEntryFormNotification(state: boolean) {
      this.onClearOrderEntryFormSubject.next(state);

      document.dispatchEvent(new CustomEvent (('onOrderEntryFormClear'), {
        detail: {
            eventValue: 'onOrderEntryFormClear'
        }
        }));
    }

    setSelectedEligibleaccount(selectedAccount: Account) {
        this.selectedEligibleaccount.next(selectedAccount);
    }

    publishEstPrincipleAmount(amount: number) {
        this.estPrincipleAmountSubject.next(amount);
    }

    getEstPrincipleAmount() {   // used for pull the value instead of listen to the change
        return this.estPrincipleAmountSubject.value;
    }

    public cancelAnnuityOrder(platform: string, acctNum: string, ordDtlKey: string, subRequestTypeCode: string): Observable<any> {
        return this.getStrategy(platform).getCancelAnnuityOrder(acctNum, ordDtlKey, subRequestTypeCode);
    }

    public fetchHoldings(platform: string, acctNum: string, firmAcct: boolean, tradeType: TradeType, includeTradingSpecificData?: boolean): void {
        if (!this.holdingsLoadInProgress) {
          this.holdingSubject$.next(null);     // have to clear the value, which is consumed by subcribers to this subject, before fetching new data
          this.holdingErrorSubject$.next(null);
          this.holdingsLoadInProgress = true;
          this.holdingLoadCompleteSubject$.next({loadComplete: false});

          this.getHoldings(platform, acctNum, firmAcct, tradeType, includeTradingSpecificData).
              subscribe(
                  (success: any) => {
                      if (success) {
                          this.holdingsSuccessCallback(success);
                      }
                  },
                  (error: any) => this.holdingsErrorCallback(error),
                  () => this.holdingsCompleteCallback()
              );
        }
    }

    private holdingsSuccessCallback(holdingsSuccessResponse: any): void {
        if ( !!holdingsSuccessResponse.response && holdingsSuccessResponse.response.positions ) {
            this.holdingSubject$.next(holdingsSuccessResponse.response.positions);
        }
    }

    private holdingsErrorCallback(error: any): void {
        console.log('errror response' + error);
        this.holdingErrorSubject$.next({hasError: true, errorCode: error.errorCode, errorMsg: error.errorMessage});
        // when the error callback is invoked, complete is not, so we need to ensure that still is handled
        this.holdingsCompleteCallback();
    }

    private holdingsCompleteCallback(): void {
        this.holdingsLoadInProgress = false;
        this.holdingLoadCompleteSubject$.next({loadComplete: true});
    }

    public getActiveThemeFromCurrentContainer(): string {
        let activeTheme;
        switch (this.currentContainer) {
          case CONTAINER_TYPE.Equity: {
            activeTheme = this.tradingEntitlements?.activeTheme?.equity === 'comfortable' ? 'comfortable' : 'compact';
            break;
          }
          case CONTAINER_TYPE.Extended: {
            activeTheme = this.tradingEntitlements?.activeTheme?.extendedHours === 'comfortable' ? 'comfortable' : 'compact';
            break;
          }
          case CONTAINER_TYPE.MutualFund: {
            activeTheme = this.tradingEntitlements?.activeTheme?.mutualFund === 'comfortable' ? 'comfortable' : 'compact';
            break;
          }
          case CONTAINER_TYPE.Option: {
            activeTheme = this.tradingEntitlements?.activeTheme?.options === 'comfortable' ? 'comfortable' : 'compact';
            break;
          }
          case CONTAINER_TYPE.Plan529: {
            activeTheme = this.tradingEntitlements?.activeTheme?.fiveTwoNineplan === 'comfortable' ? 'comfortable' : 'compact';
            break;
          }
          default: {
            activeTheme = 'compact';
            break;
          }
        }
        return activeTheme;

    }

    notifyActionPerformed(value: ActionPerformed) {
        this.actionPerformedSubject.next(value);
    }

    focusOnNotification(state: boolean) {
        this.focusOnSubject.next(state);
      }

    public getExchangeDetails(platform: string, symId: string): Observable<any> {
        return this.getStrategy(platform).getExchangeDetails(symId);
    }

    public getButtonFocus(reponse: any){
        if (reponse?.approvals?.length === 0 && reponse?.errors?.length === 0 && reponse?.warnings?.length === 0) {
            return ButtonFocus.closeBtn;
        } else if (reponse?.errors?.length > 0){
            return ButtonFocus.editOrderBtn
        } else if (reponse?.errors?.length === 0 && (reponse?.approvals?.length > 0 || reponse?.warnings?.length > 0 )) {
            return ButtonFocus.ackOrderBtn;
        }
    }

}
